/**
 * KinsCircle Phase 6D.2 — Legacy Preservation Viewer (owner/admin only).
 *
 * STABILITY.md §13–§14:
 *   - Owner/admin gated. Non-owner non-admin visitors are bounced to "/".
 *   - READ-ONLY preview surface for the Legacy Preservation Layer (6D.2).
 *   - Per-flag <Switch> toggles persist in localStorage ONLY (browser-scoped).
 *     For project-wide rollout, set the matching VITE_ENABLE_AI_* env vars.
 *   - Live snapshot stats from `readBookmarkStats()` and the collection
 *     engine builders. Nothing on this page writes to Supabase, mutates
 *     a memory row, or invents family history.
 *   - Hosts live previews of the three public 6D.2 cards (MemoryPlaylist,
 *     LegacyChapter, GenerationHandoff) so admins can verify each one
 *     renders null when its dependent flag is OFF.
 *   - "Clear all bookmarks" goes through `clearLegacyBookmarks()` and
 *     touches localStorage only — it never deletes any memory.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Library,
  Sparkles,
  ShieldCheck,
  ArrowLeft,
  AlertTriangle,
  Bookmark,
  Trash2,
  BookOpen,
  Users,
  Layers,
  Film,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  setAIFlagLocal,
  snapshotLegacyPreservationFlags,
  isLegacyTimelineEnabled,
  isLegacyCollectionsEnabled,
  isLegacyChaptersEnabled,
  isMemoryPlaylistEnabled,
  isGenerationHandoffEnabled,
  isLegacyBookmarksEnabled,
  isLegacyPreservationViewerEnabled,
} from '@/lib/ai/featureFlag';
import {
  buildEraCollections,
  buildGenerationCollection,
  buildMilestoneCollection,
  buildPersonJourneyCollection,
  buildThemeCollection,
} from '@/lib/ai/legacyCollectionEngine';
import {
  clearLegacyBookmarks,
  readBookmarkStats,
  type LegacyBookmarkKind,
} from '@/lib/ai/legacyBookmarks';
import MemoryPlaylistCard from '@/components/ai/MemoryPlaylistCard';
import LegacyChapterCard from '@/components/ai/LegacyChapterCard';
import GenerationHandoffCard from '@/components/ai/GenerationHandoffCard';

const PRESERVATION_FLAGS: {
  key: keyof ReturnType<typeof snapshotLegacyPreservationFlags>;
  label: string;
  description: string;
}[] = [
  {
    key: 'legacyCollections',
    label: 'Legacy Collections (engine)',
    description:
      'Powers milestone / person / theme / generation / era collections. Required by the three cards below.',
  },
  {
    key: 'memoryPlaylist',
    label: 'Memory Playlist Card',
    description:
      'Curated read-only playlists with a "Play playlist" CTA. No autoplay. No auto-save.',
  },
  {
    key: 'legacyChapters',
    label: 'Legacy Chapter Card',
    description:
      'Documentary-style chapter grid. Every chapter cites real source memories.',
  },
  {
    key: 'generationHandoff',
    label: 'Generation Handoff Card',
    description:
      'Cross-generation continuity. Requires ≥20-year span across ≥2 generations of anchored memories.',
  },
  {
    key: 'legacyBookmarks',
    label: 'Legacy Bookmarks (local-only)',
    description:
      'Per-card bookmark toggles. Stored in localStorage only — never written to Supabase.',
  },
  {
    key: 'legacyPreservationViewer',
    label: 'Preservation Viewer (this page)',
    description:
      'Required to render the live previews below. The page itself stays accessible to owner/admin even when this is off.',
  },
];

const KIND_META: Record<
  LegacyBookmarkKind,
  { label: string; tone: string }
> = {
  memory: {
    label: 'Memories',
    tone: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  collection: {
    label: 'Collections',
    tone: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  },
  chapter: {
    label: 'Chapters',
    tone: 'bg-pink-50 text-pink-700 border-pink-200',
  },
  playlist: {
    label: 'Playlists',
    tone: 'bg-amber-50 text-amber-700 border-amber-200',
  },
};

const formatDate = (ms: number | null): string => {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return '—';
  }
};

const LegacyPreservationViewer: React.FC = () => {
  const navigate = useNavigate();
  const { isOwner, isAdmin, isAuthenticated } = useAuth();

  // Owner/admin gate. Members get bounced.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isOwner && !isAdmin) navigate('/', { replace: true });
  }, [isAuthenticated, isOwner, isAdmin, navigate]);

  const [flags, setFlags] = useState(() => snapshotLegacyPreservationFlags());
  const refreshFlags = () => setFlags(snapshotLegacyPreservationFlags());

  const [bookmarkStats, setBookmarkStats] = useState(() => readBookmarkStats());
  const refreshBookmarkStats = () => setBookmarkStats(readBookmarkStats());
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  // Collection counts — recomputed when flags toggle, since they early-return
  // empty when the master `legacyTimeline` or `legacyCollections` flag is OFF.
  const milestones = useMemo(
    () => (isLegacyCollectionsEnabled() ? buildMilestoneCollection(20) : []),
    [flags],
  );
  const people = useMemo(
    () => (isLegacyCollectionsEnabled() ? buildPersonJourneyCollection(20) : []),
    [flags],
  );
  const themes = useMemo(
    () => (isLegacyCollectionsEnabled() ? buildThemeCollection(20) : []),
    [flags],
  );
  const generations = useMemo(
    () => (isLegacyCollectionsEnabled() ? buildGenerationCollection(20) : []),
    [flags],
  );
  const eras = useMemo(
    () => (isLegacyCollectionsEnabled() ? buildEraCollections(20) : []),
    [flags],
  );

  const totalCollections =
    milestones.length +
    people.length +
    themes.length +
    generations.length +
    eras.length;

  const handleClearBookmarks = () => {
    clearLegacyBookmarks();
    refreshBookmarkStats();
    setConfirmClearOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-white to-amber-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-slate-700"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/legacy')}
              className="border-violet-200 text-violet-700 hidden md:inline-flex"
            >
              <Film className="w-4 h-4 mr-1.5" />
              Phase 6D Viewer
            </Button>
            <Badge className="bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" />
              Owner / Admin only
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <Library className="w-7 h-7 text-fuchsia-700" />
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-slate-900">
            Legacy Preservation Viewer
          </h1>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-fuchsia-700 bg-fuchsia-100 px-2 py-0.5 rounded-full">
            Phase 6D.2 · Beta
          </span>
        </div>
        <p className="text-sm text-slate-600 mb-8 max-w-3xl">
          Read-only preview of the Legacy Preservation Layer — guided story
          collections, documentary-style chapters, and generation-handoff
          cards. Toggle flags to test individual surfaces in this browser.
          Nothing on this page writes to Supabase, mutates a memory, or
          invents family history.
        </p>

        {/* Stability strip */}
        <div className="mb-8 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Flag toggles below persist in <strong>this browser only</strong>.
            For project-wide rollout, set the matching{' '}
            <code>VITE_ENABLE_AI_*</code> env vars and redeploy. All Phase
            6D.2 features additionally require the master{' '}
            <code>legacyTimeline</code> flag from Phase 6D to be ON.
          </div>
        </div>

        {/* Master-flag warning when 6D is off */}
        {!isLegacyTimelineEnabled() && (
          <Card className="mb-8 border-rose-200 bg-rose-50/50">
            <CardContent className="py-4 flex items-start gap-2 text-sm text-rose-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                The master <strong>Legacy Timeline</strong> flag from Phase 6D
                is OFF. All Phase 6D.2 helpers will safely return empty data
                until you flip it ON in the{' '}
                <button
                  className="underline font-semibold"
                  onClick={() => navigate('/admin/legacy')}
                >
                  Phase 6D viewer
                </button>
                .
              </div>
            </CardContent>
          </Card>
        )}

        {/* Flag toggles */}
        <Card className="mb-8 border-fuchsia-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-fuchsia-600" />
              Phase 6D.2 Feature Flags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {PRESERVATION_FLAGS.map((f) => (
                <label
                  key={f.key}
                  htmlFor={`flag-${f.key}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-fuchsia-300 cursor-pointer transition-colors"
                >
                  <Switch
                    id={`flag-${f.key}`}
                    checked={flags[f.key]}
                    onCheckedChange={(v) => {
                      setAIFlagLocal(f.key, v);
                      refreshFlags();
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <Label
                      htmlFor={`flag-${f.key}`}
                      className="text-sm font-semibold text-slate-900 cursor-pointer"
                    >
                      {f.label}
                    </Label>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {f.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Collection snapshot stats */}
        <Card className="mb-8 border-fuchsia-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-1.5">
              <Layers className="w-4 h-4 text-fuchsia-600" />
              Collection Engine Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat
                label="Total collections"
                value={String(totalCollections)}
              />
              <Stat label="Milestones" value={String(milestones.length)} />
              <Stat label="People journeys" value={String(people.length)} />
              <Stat label="Themes" value={String(themes.length)} />
              <Stat
                label="Generation handoffs"
                value={String(generations.length)}
              />
              <Stat label="Eras" value={String(eras.length)} />
              <Stat
                label="Engine flag"
                value={isLegacyCollectionsEnabled() ? 'ON' : 'OFF'}
              />
              <Stat
                label="Master flag"
                value={isLegacyTimelineEnabled() ? 'ON' : 'OFF'}
              />
            </div>
            <p className="text-[11px] text-slate-400 mt-3">
              All numbers are recomputed at runtime from the existing semantic
              index. No collection is persisted to Supabase.
            </p>
          </CardContent>
        </Card>

        {/* Bookmark stats + clear */}
        <Card className="mb-8 border-fuchsia-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-1.5">
              <Bookmark className="w-4 h-4 text-fuchsia-600" />
              Local Bookmarks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Total" value={String(bookmarkStats.total)} />
              <Stat label="Oldest" value={formatDate(bookmarkStats.oldest)} />
              <Stat label="Newest" value={formatDate(bookmarkStats.newest)} />
              <Stat
                label="Bookmarks flag"
                value={isLegacyBookmarksEnabled() ? 'ON' : 'OFF'}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              {(Object.keys(KIND_META) as LegacyBookmarkKind[]).map((k) => (
                <div
                  key={k}
                  className={`rounded-lg border px-3 py-2 text-xs flex items-center justify-between ${KIND_META[k].tone}`}
                >
                  <span className="font-semibold">{KIND_META[k].label}</span>
                  <span className="font-mono">
                    {bookmarkStats.byKind[k] ?? 0}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-slate-500 flex-1 min-w-[200px]">
                Bookmarks are stored in <code>localStorage</code> under{' '}
                <code>kinscircle.legacy.bookmarks.v1</code>. They never sync
                to Supabase and never reference memory captions or storage
                URLs — only memory IDs.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmClearOpen(true)}
                disabled={bookmarkStats.total === 0}
                className="border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Clear all bookmarks
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Live previews */}
        {isLegacyPreservationViewerEnabled() ? (
          <div className="space-y-2">
            <h2 className="font-serif text-xl font-bold text-slate-900 mt-4 mb-1">
              Live previews
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              These render exactly as they will on the dashboard. Each card
              renders null when its dependent flag is OFF or no qualifying
              collections exist.
            </p>

            <PreviewWrap
              icon={<BookOpen className="w-4 h-4 text-amber-700" />}
              title="MemoryPlaylistCard"
              subtitle={`Flag: ${
                isMemoryPlaylistEnabled() ? 'ON' : 'OFF'
              } · ${themes.length + people.length + milestones.length} candidate playlists`}
            >
              <MemoryPlaylistCard />
            </PreviewWrap>

            <PreviewWrap
              icon={<Layers className="w-4 h-4 text-pink-700" />}
              title="LegacyChapterCard"
              subtitle={`Flag: ${
                isLegacyChaptersEnabled() ? 'ON' : 'OFF'
              } · ${eras.length} eras · ${themes.length} themes`}
            >
              <LegacyChapterCard />
            </PreviewWrap>

            <PreviewWrap
              icon={<Users className="w-4 h-4 text-fuchsia-700" />}
              title="GenerationHandoffCard"
              subtitle={`Flag: ${
                isGenerationHandoffEnabled() ? 'ON' : 'OFF'
              } · ${generations.length} cross-generation traditions`}
            >
              <GenerationHandoffCard />
            </PreviewWrap>
          </div>
        ) : (
          <Card className="border-dashed border-slate-300 bg-slate-50/50">
            <CardContent className="py-6 text-center text-sm text-slate-500">
              Toggle the <strong>Preservation Viewer</strong> flag above to
              render live previews of the three Phase 6D.2 cards inline.
            </CardContent>
          </Card>
        )}

        <p className="text-[11px] text-slate-400 mt-8">
          STABILITY.md §13–§14: Phase 6D.2 is read-only. No memory rows,
          captions, uploads, billing, RLS, or relationship engine state is
          mutated by this page or by any of its features. All bookmark
          operations are local to this browser.
        </p>
      </div>

      <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all bookmarks?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every saved bookmark in <em>this browser</em>{' '}
              ({bookmarkStats.total} total). It does not delete any memory,
              chapter, playlist, or family graph data — bookmarks are
              local-only metadata. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearBookmarks}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Clear bookmarks
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg bg-white border border-slate-200 p-3">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="text-lg font-bold text-slate-900 mt-0.5 truncate">
      {value}
    </div>
  </div>
);

const PreviewWrap: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, children }) => (
  <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/60">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-700">{title}</div>
        <div className="text-[10px] text-slate-500 truncate">{subtitle}</div>
      </div>
    </div>
    <div className="p-2">{children}</div>
  </div>
);

export default LegacyPreservationViewer;
